import { FC } from 'react'
import clsx from 'clsx'
import type { TableHeaderCellProps } from './types'
import { SortWrapper } from '../SortWrapper'
import { Typography } from '../../../Typography'
import { Tooltip } from '../../../Tooltip'
import InfoCircle from '@sylveraio/untitled-ui-icons-react/build/esm/InfoCircle'
import ArrowDown from '@sylveraio/untitled-ui-icons-react/build/esm/ArrowDown'
import ArrowUp from '@sylveraio/untitled-ui-icons-react/build/esm/ArrowUp'
import { getPinningClassNames, getPinningStyles } from '../../utils/getPinning'

export const TableHeaderCell: FC<TableHeaderCellProps> = ({
  column,
  id,
  isSortable = false,
  justify = 'left',
  onSort,
  sortDirection,
  tooltipProps,
  value,
  condensed = false,
  transparentBackground,
}) => {
  const isPinned = column.getIsPinned()
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left')

  // 150 is the default column width defined by TanStack. If no custom width is supplied, don't set a width.
  const width = column.getSize() === 150 ? undefined : column.getSize()

  return (
    <th
      key={id}
      className={clsx('h-1', getPinningClassNames(column), {
        'bg-medium': isPinned,
        'bg-transparent !-z-10': transparentBackground,
      })}
      style={{
        ...getPinningStyles(column),
        // Min and max width are needed as the table uses auto-layout by default
        minWidth: width,
        maxWidth: width,
      }}
    >
      <SortWrapper isSortable={isSortable} onSort={onSort}>
        <div
          className={clsx('py-4 h-full flex gap-x-1 items-center', {
            'justify-end': justify === 'right',
            'justify-center': justify === 'center',
            'border-default border-r': isLastLeftPinnedColumn,
            'px-3': condensed,
            'px-4': !condensed,
          })}
        >
          <Typography
            size="xs"
            weight="medium"
            className={clsx('text-subtle', {
              'text-left': justify === 'left',
              'text-right': justify === 'right',
            })}
          >
            {value}
          </Typography>
          {typeof tooltipProps !== 'undefined' && (
            <Tooltip condensed {...tooltipProps}>
              <InfoCircle
                height={16}
                width={16}
                className="stroke-icon-subtle"
              />
            </Tooltip>
          )}
          {isSortable ? (
            <>
              {sortDirection === 'asc' && (
                <ArrowDown
                  height={16}
                  width={16}
                  className="stroke-icon-subtle"
                />
              )}
              {sortDirection === 'desc' && (
                <ArrowUp
                  height={16}
                  width={16}
                  className="stroke-icon-subtle"
                />
              )}
              {typeof sortDirection === 'undefined' && (
                <div className="h-4 w-4" />
              )}
            </>
          ) : null}
        </div>
      </SortWrapper>
    </th>
  )
}
